import { Fragment } from "react"

// ** Third Party Components
import axios from "axios"
import { Locale } from "react-joyride"
import { useTranslation } from "react-i18next"
import { useMutation } from "@tanstack/react-query"
import { ChevronDown, LogOut } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { destroyCookie, parseCookies, setCookie } from "nookies"

// ** Internal Element
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "@components/common/menu-bar"

// ** Page Routes
import { LOGIN_PAGE_ROUTE, PROFILE_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Api Routes
import { SERVER_URL, USER_LANGUAGE_ENDPOINT } from "@pma/utils/api-routes"

// ** Types
import { DictType } from "@pma/types"

// ** Store
import { useAuth } from "@stores/auth-store"
import { useModal } from "@stores/modal-store"

// ** Constants
import { locales } from "@pma/locale/dictionaries"
import { ACCESS_TOKEN, LOCALE } from "@pma/utils/constants"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@hooks/use-translator"

const UserMenu = () => {
  const modal = useModal()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const { translate, locale } = useTranslator()
  const { authConfig, signOut, signIn } = useAuth()

  const isHebrew = locale === "he"
  const username = authConfig?.name
  const jobTitle = authConfig?.job_title
  const userAvatar = authConfig?.user_picture
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const changeLocale = (locale: Locale) => {
    i18n.changeLanguage(locale as string)

    setCookie(null, LOCALE, locale as string, {
      maxAge: 1000 * 60 * 60 * 24 * 360,
      sameSite: "strict",
      path: "/",
    })

    changeUserLanguageMutation.mutate(locale)
  }

  const changeUserLanguage = async (locale: Locale) => {
    const response = await axios.post(
      SERVER_URL + USER_LANGUAGE_ENDPOINT,
      { selected_language: locale },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return response.data
  }

  const changeUserLanguageMutation = useMutation({
    mutationKey: ["user-language"],
    mutationFn: changeUserLanguage,
    onSuccess: (data) => {
      signIn({ ...data })
      toast({
        title: translate("Success"),
        description: `${translate("Default language")} ${translate("updated")}`,
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorResponse || "Unable to update default language"),
        variant: "error",
      })
    },
  })

  const handleClick = () => {
    modal.onOpen()
    modal.setContext("workspace")
  }

  const logOut = () => {
    signOut()
    destroyCookie(undefined, ACCESS_TOKEN)
    document.cookie = ACCESS_TOKEN + "=; Max-Age=0"
    navigate(LOGIN_PAGE_ROUTE)
  }

  return (
    <div className='z-50 hidden items-center md:flex' id='user-profile-section'>
      <Menubar className='border-none bg-none'>
        <MenubarMenu>
          <MenubarTrigger className='flex h-[50px] w-[200px] cursor-pointer items-center justify-between rounded-full border bg-none p-1'>
            <img
              alt=''
              src={`${userAvatar?.url || process.env.PUBLIC_URL + "/user-profile.svg"}`}
              className='h-[35px] w-[35px] rounded-full object-cover'
            />
            <div className={cn("flex flex-1 flex-col items-start", isHebrew ? "pr-2" : "pl-2")}>
              <h4
                className={cn(
                  "w-[14ch] truncate text-ellipsis font-light",
                  isHebrew ? "text-right" : "text-left",
                )}
              >
                {username ? username : "-"}
              </h4>
              <span className='text-xs text-slate-400'>
                {jobTitle ? jobTitle?.name : translate("Missing job title")}
              </span>
            </div>
            <div className=''>
              <ChevronDown className='transition-all ease-out hover:scale-110' />
            </div>
          </MenubarTrigger>
          <MenubarContent className='mx-2 cursor-pointer bg-background'>
            <Link to={PROFILE_PAGE_ROUTE}>
              <MenubarItem className='cursor-pointer'>{translate("Profile")}</MenubarItem>
            </Link>
            <MenubarSeparator />
            <MenubarSub>
              <MenubarSubTrigger className='cursor-pointer'>
                {translate("Language")}
              </MenubarSubTrigger>
              <MenubarSubContent className='bg-background'>
                {locales.map((language: DictType, index: number) => (
                  <Fragment key={index}>
                    <MenubarItem
                      onClick={() => changeLocale(language.locale)}
                      className={cn(
                        "flex w-full cursor-pointer items-center justify-between",
                        locale === language?.locale && "font-semibold text-primary",
                      )}
                    >
                      <span>{translate(language.name)}</span>
                      <span>{language.emoji}</span>{" "}
                    </MenubarItem>
                    {index < locales.length - 1 && <MenubarSeparator />}
                  </Fragment>
                ))}
              </MenubarSubContent>
            </MenubarSub>
            <MenubarSeparator />
            <MenubarItem onClick={handleClick} className='cursor-pointer'>
              {translate("Switch Workspace")}
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem className='cursor-pointer text-red-500' onClick={() => logOut()}>
              {translate("Logout")}
              <MenubarShortcut>
                <LogOut size={16} className='text-red-500' />
              </MenubarShortcut>
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
    </div>
  )
}

export default UserMenu
